import { useState, useRef, useEffect } from 'react';
import { Send, Smile } from 'lucide-react';
import { usePlayerStore } from '@/store/playerStore';
import { useRoomStore } from '@/store/roomStore';
import type { ChatMessage } from '@/types';

const EMOJI_REACTIONS = ['🍿', '❤️', '😂', '👏', '🔥', '🎉'];

interface RoomChatProps {
  onSendMessage: (text: string) => void;
  onSendReaction: (emoji: string) => void;
}

export function RoomChat({ onSendMessage, onSendReaction }: RoomChatProps) {
  const { chatMessages } = usePlayerStore();
  const { participantId } = useRoomStore();
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleSend = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = inputText.trim();
    if (!trimmed) return;
    onSendMessage(trimmed);
    setInputText('');
  };

  return (
    <div className="flex flex-col h-full text-xs font-sans">
      {/* Messages Stream */}
      <div className="flex-1 overflow-y-auto space-y-2.5 p-3.5 pr-2 contain-overscroll">
        {chatMessages.length === 0 ? (
          <div className="text-center py-8 text-cinema-500 space-y-1">
            <p>No messages yet.</p>
            <p className="text-[11px]">Chat and reactions appear here in sync.</p>
          </div>
        ) : (
          chatMessages.map((msg) => {
            if (msg.isSystem) {
              return (
                <div
                  key={msg.id}
                  className="text-center py-1 px-2 text-[11px] text-cinema-400 bg-cinema-950/60 border border-cinema-700/40 rounded italic"
                >
                  {msg.text}
                </div>
              );
            }

            const isMe = msg.senderId === participantId;

            return (
              <div
                key={msg.id}
                className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}
              >
                <div className="flex items-center gap-1.5 mb-0.5 text-[10px] text-cinema-400 px-1">
                  <span className={isMe ? 'text-lamp font-medium' : 'text-cinema-300'}>
                    {isMe ? 'You' : msg.senderName}
                  </span>
                  <span>·</span>
                  <span>
                    {new Date(msg.timestamp).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
                <div
                  className={`max-w-[85%] px-3 py-1.5 rounded-lg text-xs leading-relaxed break-words shadow-xs ${
                    isMe
                      ? 'bg-lamp text-cinema-950 font-medium rounded-tr-none'
                      : 'bg-cinema-850 text-cinema-100 border border-cinema-700/60 rounded-tl-none'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Reaction Bar */}
      <div className="px-3 py-1.5 hairline-t bg-cinema-950/70 flex items-center justify-between gap-1 flex-shrink-0">
        <span className="text-[10px] font-mono uppercase tracking-wider text-cinema-500 mr-1 flex items-center gap-1">
          <Smile className="w-3 h-3 text-lamp" />
          <span>React:</span>
        </span>
        <div className="flex items-center gap-1">
          {EMOJI_REACTIONS.map((emoji) => (
            <button
              key={emoji}
              onClick={() => onSendReaction(emoji)}
              className="text-base p-1 hover:scale-125 transition-transform duration-150 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-lamp rounded"
              title={`Send ${emoji}`}
              aria-label={`Send ${emoji} reaction`}
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>

      {/* Message Input Form */}
      <form
        onSubmit={handleSend}
        className="p-3 hairline-t bg-cinema-900 flex items-center gap-2 flex-shrink-0"
      >
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder="Send message to room…"
          maxLength={250}
          className="flex-1 bg-cinema-950 border border-cinema-700 rounded-md px-3 py-1.5 text-xs text-cinema-100 placeholder-cinema-500 focus:outline-none focus:border-lamp focus:ring-1 focus:ring-lamp"
        />
        <button
          type="submit"
          disabled={!inputText.trim()}
          className="p-1.5 rounded-md bg-lamp hover:bg-lamp-bright disabled:opacity-40 disabled:cursor-not-allowed text-cinema-950 transition-all duration-150 enabled:active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp shadow-xs"
          aria-label="Send message"
        >
          <Send className="w-3.5 h-3.5 fill-current" />
        </button>
      </form>
    </div>
  );
}

